import { Loader2, X } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";

const formatLevel = (value) =>
	value === null || value === undefined || !Number.isFinite(Number(value))
		? "—"
		: Number(value).toFixed(1).replace(".", ",");

const formatValue = (exercise) => {
	if (exercise?.value === null || exercise?.value === undefined) return "—";
	const unit = exercise.unit === "kg" ? " kg" : "";
	return `${String(exercise.value).replace(".", ",")}${unit}`;
};

const average = (values) => {
	const numbers = values.filter((value) => Number.isFinite(Number(value)));
	if (numbers.length === 0) return null;
	return numbers.reduce((sum, value) => sum + Number(value), 0) / numbers.length;
};

/**
 * Lo stesso atleta letto sulle tabelle di due categorie di peso (D6b).
 *
 * Serve al coach quando deve decidere dove far gareggiare un atleta: con i
 * massimali di oggi, che livello avrebbe nella categoria sopra o sotto? I
 * valori sono gli stessi, cambia solo la tabella con cui si leggono. Una riga
 * per esercizio con un valore, e per ogni dominio la media dei livelli.
 *
 * Props: base e other (le liste di domini del profilo, nella categoria
 * dell'atleta e in quella di confronto), baseLabel, otherLabel, loading,
 * onClose.
 */
export function WeightClassComparison({
	base = [],
	other = null,
	baseLabel,
	otherLabel,
	loading = false,
	onClose,
}) {
	const { t } = useLanguage();
	const otherById = {};
	(other || []).forEach((domain) =>
		(domain.exercises || []).forEach((exercise) => {
			otherById[exercise.config_id || exercise.exercise_id] = exercise;
		}),
	);

	const domains = (base || [])
		.map((domain) => ({
			...domain,
			rows: (domain.exercises || [])
				.filter((exercise) => exercise.value !== null && exercise.value !== undefined)
				.map((exercise) => ({
					exercise,
					other: otherById[exercise.config_id || exercise.exercise_id] || null,
				})),
		}))
		.filter((domain) => domain.rows.length > 0);

	const baseName = baseLabel || t("wccGenderTable");

	return (
		<Card className="bg-card border-border rounded-sm" data-testid="weight-class-comparison">
			<CardContent className="p-3 space-y-3">
				<div className="flex items-start justify-between gap-2">
					<div className="min-w-0">
						<p className="font-heading text-sm">
							{t("wccTitle")
								.replace("{base}", baseName)
								.replace("{other}", otherLabel || "")}
						</p>
						<p className="text-[11px] text-muted-foreground">{t("wccHint")}</p>
					</div>
					{onClose && (
						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7 rounded-sm shrink-0"
							aria-label={t("close")}
							onClick={onClose}
						>
							<X className="w-3.5 h-3.5" />
						</Button>
					)}
				</div>

				{loading || !other ? (
					<div className="flex items-center gap-2 text-xs text-muted-foreground">
						<Loader2 className="w-3.5 h-3.5 animate-spin" />
						{t("loading")}
					</div>
				) : domains.length === 0 ? (
					<p className="text-xs text-muted-foreground">{t("wccNoValues")}</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full min-w-[420px] text-xs">
							<thead>
								<tr className="text-left text-muted-foreground border-b border-border">
									<th className="py-1.5 pr-2 font-medium">{t("pmColExercise")}</th>
									<th className="py-1.5 pr-2 font-medium">{t("wccValue")}</th>
									<th className="py-1.5 pr-2 font-medium">{baseName}</th>
									<th className="py-1.5 font-medium">{otherLabel}</th>
								</tr>
							</thead>
							<tbody>
								{domains.map((domain) => {
									const baseAverage = average(
										domain.rows.map((row) => row.exercise.score_value),
									);
									const otherAverage = average(
										domain.rows.map((row) => row.other?.score_value),
									);
									return [
										<tr key={`${domain.domain_id}_head`} className="border-b border-border/60">
											<td className="pt-2 pb-1 font-medium text-foreground" colSpan={2}>
												{domain.name}
											</td>
											<td className="pt-2 pb-1 font-medium tabular-nums">
												{formatLevel(baseAverage)}
											</td>
											<td className="pt-2 pb-1 font-medium tabular-nums">
												{formatLevel(otherAverage)}
											</td>
										</tr>,
										...domain.rows.map(({ exercise, other: compared }) => {
											const baseScore = exercise.score_value;
											const otherScore = compared?.score_value;
											const delta =
												Number.isFinite(Number(baseScore)) &&
												Number.isFinite(Number(otherScore))
													? Number(otherScore) - Number(baseScore)
													: null;
											const tone =
												delta === null || Math.abs(delta) < 0.05
													? ""
													: delta > 0
														? "text-emerald-400"
														: "text-amber-300";
											return (
												<tr
													key={exercise.config_id || exercise.exercise_id}
													className="border-b border-border/30"
												>
													<td className="py-1 pr-2">{exercise.exercise_name}</td>
													<td className="py-1 pr-2 tabular-nums text-muted-foreground">
														{formatValue(exercise)}
													</td>
													<td className="py-1 pr-2 tabular-nums">{formatLevel(baseScore)}</td>
													<td className={`py-1 tabular-nums ${tone}`}>
														{formatLevel(otherScore)}
														{compared &&
															compared.scoring_basis &&
															compared.scoring_basis !== "weight_class" && (
																<span className="ml-1 text-[10px] text-muted-foreground">
																	{t("wccGenderTableShort")}
																</span>
															)}
													</td>
												</tr>
											);
										}),
									];
								})}
							</tbody>
						</table>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
